import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  CalculatorIcon,
  ChartColumnIcon,
  CirclePlayIcon,
  LightbulbIcon,
  PaperclipIcon,
  RepeatIcon,
  TableIcon,
  WrenchIcon,
  type LucideIcon,
} from "lucide-react";
import { iconsByTypename } from "@/objects/@icons";
import { Objects } from "@/objects";
import { ObjectType } from "@/objects/@types";
import type { ToolCallContent, NodeItem } from "./ToolCallCard.types";
import { MessageContextObject } from "@/__generated__/graphql";
import { ExploreActionRow } from "./ToolCallCardExploreActionRow";
import { ThoughtActionRow } from "./ToolCallCardThoughtActionRow";

/**
 * Utility functions for ToolCallCard components.
 */

// ── Workflow lookup output normalization ──────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nodeItemFromRecord(node: Record<string, unknown>): NodeItem {
  return {
    ...node,
    identifier: node.identifier as string | undefined,
    __typename:
      (node.__typename as string | undefined) ??
      (node.__graphql_typename as string | undefined),
  };
}

/**
 * Agent tool calls persist ``{ item }`` / ``{ items }``. Workflow lookup nodes
 * store the raw evaluator output (plain dict or array with ``identifier`` and
 * ``__graphql_typename``).
 *
 * Normalization happens at read time — not in ``record_workflow_node`` — because
 * ``Execution.node_snapshots`` rebuilds cumulative context directly from
 * ``MessageContent.tool_output``. Wrapping lookup rows at write time would change
 * snapshot shape and break variable paths that expect raw arrays/dicts.
 *
 * ``resolvedNodes`` (GraphQL ``content.nodes``) is a fallback when the JSON
 * shape is empty but MessageContext links were written.
 */
export function normalizeToolOutputForDisplay(
  toolOutput: unknown,
  resolvedNodes?: NodeItem[] | null,
): Record<string, unknown> | null {
  if (Array.isArray(toolOutput)) {
    if (toolOutput.length === 0) return null;
    const items = toolOutput.filter(isRecord).map(nodeItemFromRecord);
    return items.length === 1 ? { item: items[0] } : { items };
  }

  if (isRecord(toolOutput)) {
    if (toolOutput.item || toolOutput.items) return toolOutput;
    if (typeof toolOutput.identifier === "string") {
      return { item: nodeItemFromRecord(toolOutput) };
    }
    return toolOutput;
  }

  if (resolvedNodes && resolvedNodes.length > 0) {
    const items = resolvedNodes.map((node) => nodeItemFromRecord(node));
    return items.length === 1 ? { item: items[0] } : { items };
  }

  return null;
}

// ── Typename inference ────────────────────────────────────────────────────────

const WORKFLOW_NODE_TYPE_TO_TYPENAME: Record<string, string> = {
  TRIGGER: "RuleNodeTriggerObject",
  ACTION_LOOKUP: "RuleNodeActionLookupObject",
  ACTION_PROMPT: "RuleNodeActionPromptObject",
  ACTION_THREAD: "RuleNodeActionThreadObject",
  SWITCH: "RuleNodeActionMatchDocumentObject",
};

/**
 * Infer typename from tool name if not present.
 * e.g., "create_contact" → "ContactObject"
 */
export function inferTypename(
  item: NodeItem,
  toolName?: string | null,
): string {
  const raw =
    item.__typename ??
    (item.__graphql_typename as string | undefined) ??
    inferFromToolName(toolName);
  if (raw === "RuleNodeObject" || raw === "WorkflowNodeObject") {
    const nodeType =
      (item.ruleNodeType as string) ?? (item.type as string) ?? null;
    if (nodeType && WORKFLOW_NODE_TYPE_TO_TYPENAME[nodeType]) {
      return WORKFLOW_NODE_TYPE_TO_TYPENAME[nodeType];
    }
    return "RuleNodeActionLookupObject";
  }
  return raw;
}

function inferFromToolName(toolName?: string | null): string {
  if (!toolName) return "Object";
  const parts = toolName.split("_");
  if (parts.length >= 2) {
    const serviceParts = parts.slice(1);
    const serviceName = serviceParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
    return serviceName + "Object";
  }
  return "Object";
}

// ── Title value extraction ────────────────────────────────────────────────────

/**
 * Get the title field value from an item using the object definition.
 */
export function getTitleValue(
  item: NodeItem,
  objectDef?: ObjectType<any>,
): string {
  if (objectDef?.titleFieldKey) {
    const value = item[objectDef.titleFieldKey as string];
    if (value) return String(value);
  }
  if (objectDef?.getTitle) {
    const value = objectDef.getTitle(item as any);
    if (value) return String(value);
  }
  // Fallback to common name fields
  return String(
    item.name ||
      item.description ||
      item.title ||
      item.identifier?.slice(0, 8) ||
      "Unknown",
  );
}

// ── Date formatting ───────────────────────────────────────────────────────────

/**
 * Format date for display.
 */
export function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// ── Tool name formatting ──────────────────────────────────────────────────────

/**
 * Format tool name for display.
 * e.g., "query_contacts" → "Query contacts"
 */
export function formatToolName(
  name: string,
  children?: ToolCallContent[],
  toolInput?: Record<string, unknown> | null,
  status?: string | null,
): string {
  // Never "Thinking": that word belongs to the think tool's own row, and a
  // tool call read as thinking before its name arrived.
  if (!name || name === "unknown") return "Tool call";

  // A row a tool made for one of the things it is working through says which.
  const label = toolInput?.label;
  if (typeof label === "string" && label.trim()) return label.trim();

  // What a call of the model's is doing, said as work rather than as the
  // method it happens to be named after: while it runs, and once it is over.
  const finished = status === "done";
  const counted = (key: string, one: string, many: string) => {
    const items = toolInput?.[key];
    const count = Array.isArray(items) ? items.length : null;
    if (count === null) return many;
    return `${count} ${count === 1 ? one : many}`;
  };
  if (name === "read_files_messages") {
    return `${finished ? "Read" : "Reading"} ${counted("files", "file you sent", "files you sent")}`;
  }
  // A chart is what it is a chart of: the title the call was made with, which
  // is also what the chart itself would have written across its top.
  if (name === "chart") {
    const chartTitle = toolInput?.title;
    if (typeof chartTitle === "string" && chartTitle.trim()) {
      return chartTitle.trim();
    }
    return "Chart";
  }

  if (name === "present_results") {
    const summary =
      (toolInput?.summary as string | undefined) ??
      (toolInput?.presentation_summary as string | undefined);
    if (summary?.trim()) return summary.trim();
    return "Present results";
  }

  // Only capitalize first letter, keep rest lowercase
  const capitalize = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

  // Singularize: remove trailing 's' (simple version)
  const singularize = (s: string) => {
    if (s.endsWith("ies")) return s.slice(0, -3) + "y";
    if (
      s.endsWith("ses") ||
      s.endsWith("xes") ||
      s.endsWith("ches") ||
      s.endsWith("shes")
    )
      return s.slice(0, -2);
    if (s.endsWith("s") && !s.endsWith("ss")) return s.slice(0, -1);
    return s;
  };

  // Pluralize: add 's' if not already plural
  const pluralize = (s: string) => (s.endsWith("s") ? s : s + "s");

  // Get item count from children (for bulk operations)
  const getItemCount = (): number => {
    if (!children) return 0;
    const toolCalls = children.filter((c) => c.type === "tool_call");
    return toolCalls.length;
  };

  if (name === "for_each_agent") {
    const itemCount = getItemCount();
    const isSingular = itemCount === 1;

    // Get source_type from toolInput or children
    const sourceType = toolInput?.source_type as string | undefined;
    if (sourceType) {
      const singularService = singularize(sourceType.toLowerCase());
      const service = isSingular ? singularService : pluralize(singularService);
      return `Loop ${service}`;
    }
    // Fallback: try to infer from first child tool call
    const firstToolCall = children?.find(
      (c) => c.type === "tool_call" && c.toolName,
    );
    if (firstToolCall?.toolName) {
      const parts = firstToolCall.toolName.split("_");
      if (parts.length >= 2) {
        const rawService = parts.slice(1).join(" ").toLowerCase();
        // Always singularize first, then pluralize if needed
        const singularService = singularize(rawService);
        const service = isSingular
          ? singularService
          : pluralize(singularService);
        return `Loop ${service}`;
      }
    }
    return "Loop";
  }

  // "bulk_create_documents" → "Creating 2 documents" / "Created 2 documents":
  // what it is writing, counted from the rows it was called with.
  const bulkCreate = /^bulk_create_(.+)$/.exec(name);
  if (bulkCreate) {
    const service = singularize(
      bulkCreate[1].split("_").join(" ").toLowerCase(),
    );
    return `${finished ? "Created" : "Creating"} ${counted("creates", service, pluralize(service))}`;
  }

  // "bulk_update_transactions" → "Bulk update transactions";
  // "bulk_update_charted_accounts_where" → "Bulk update matching charted accounts"
  const bulkUpdate = /^bulk_update_(.+?)(_where)?$/.exec(name);
  if (bulkUpdate) {
    const service = pluralize(
      singularize(bulkUpdate[1].split("_").join(" ").toLowerCase()),
    );
    return `Bulk update ${bulkUpdate[2] ? "matching " : ""}${service}`;
  }

  // Regular tool calls: "query_contacts" → "Query contacts" (plural for query/list)
  // "create_transaction" → "Create transaction" (singular for others)
  const parts = name.split("_");
  if (parts.length >= 2) {
    const method = parts[0].toLowerCase();
    const rawService = parts.slice(1).join(" ");
    // Query and list operations should be plural, others singular
    const isPluralMethod = method === "query" || method === "list";
    const singularService = singularize(rawService);
    const service = isPluralMethod
      ? pluralize(singularService)
      : singularService;
    return `${capitalize(method)} ${service.toLowerCase()}`;
  }
  return name
    .split("_")
    .map((w, i) => (i === 0 ? capitalize(w) : w.toLowerCase()))
    .join(" ");
}

/** The few calls that are not about one kind of record. */
const ICONS_BY_TOOL: Record<string, LucideIcon> = {
  chart: ChartColumnIcon,
  summarize: CalculatorIcon,
  calculate: CalculatorIcon,
  think: LightbulbIcon,
  present_results: TableIcon,
  read_files_messages: PaperclipIcon,
  for_each_agent: RepeatIcon,
  get_service_tools: WrenchIcon,
};

/**
 * The icon a tool call carries, which says what it is about.
 *
 * A call is nearly always about one kind of record - documents, transactions -
 * and the object registry already knows what each of those looks like, so the
 * icon comes from there rather than from a list kept per tool. Only the calls
 * that are about no record in particular are named here.
 */
export function toolIcon(toolName?: string | null): LucideIcon {
  if (!toolName) return CirclePlayIcon;
  const named = ICONS_BY_TOOL[toolName];
  if (named) return named;

  // "bulk_create_documents" / "bulk_update_transactions_where" / "query_contacts":
  // whatever is left once the verb and the "where" are taken off is the record.
  const service = toolName
    .replace(/^(bulk_)?(query|create|update|delete|list|get|import)_/, "")
    .replace(/_where$/, "");
  const typename =
    service
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("")
      .replace(/ies$/, "y")
      .replace(/([^s])s$/, "$1") + "Object";

  return (
    iconsByTypename[typename as keyof typeof iconsByTypename] ?? CirclePlayIcon
  );
}

// ── Typewriter hook ───────────────────────────────────────────────────────────

/**
 * Hook for streaming text animation (typewriter effect).
 */
export function useTypewriter(text: string) {
  const [displayedText, setDisplayedText] = useState("");
  const displayedLengthRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef(0);
  const lastTextRef = useRef("");
  const lastTextUpdateRef = useRef(Date.now());
  const accumulatedCharsRef = useRef(0);
  const streamStartTimeRef = useRef<number | null>(null);
  const totalCharsReceivedRef = useRef(0);
  const computedCharsPerSecondRef = useRef(30);

  const minCharsPerSecond = 5;
  const comfortableBuffer = 40;
  const minBuffer = 3;
  const streamEndDelay = 1500;
  const catchUpSpeed = 80;

  useEffect(() => {
    if (text !== lastTextRef.current) {
      const now = Date.now();
      if (!text.startsWith(displayedText)) {
        displayedLengthRef.current = 0;
        accumulatedCharsRef.current = 0;
        streamStartTimeRef.current = now;
        totalCharsReceivedRef.current = text.length;
      } else {
        const newChars = text.length - lastTextRef.current.length;
        if (newChars > 0) {
          accumulatedCharsRef.current += newChars;
          totalCharsReceivedRef.current += newChars;
          if (streamStartTimeRef.current) {
            const elapsed = (now - streamStartTimeRef.current) / 1000;
            if (elapsed > 0) {
              computedCharsPerSecondRef.current =
                totalCharsReceivedRef.current / elapsed;
            }
          }
        }
      }
      lastTextRef.current = text;
      lastTextUpdateRef.current = now;
    }
  }, [text, displayedText]);

  useEffect(() => {
    const animate = (time: number) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastTextUpdateRef.current;
      const streamLikelyEnded = timeSinceLastUpdate > streamEndDelay;
      const buffer = text.length - displayedLengthRef.current;

      let charsPerSecond: number;
      if (streamLikelyEnded && buffer > 0) {
        charsPerSecond = catchUpSpeed;
      } else if (buffer > comfortableBuffer) {
        charsPerSecond = Math.max(
          minCharsPerSecond,
          computedCharsPerSecondRef.current * 1.5,
        );
      } else if (buffer < minBuffer && !streamLikelyEnded) {
        charsPerSecond = minCharsPerSecond;
      } else {
        charsPerSecond = Math.max(
          minCharsPerSecond,
          computedCharsPerSecondRef.current,
        );
      }

      if (displayedLengthRef.current < text.length) {
        if (lastFrameTimeRef.current === 0) {
          lastFrameTimeRef.current = time;
        }
        const deltaTime = (time - lastFrameTimeRef.current) / 1000;
        lastFrameTimeRef.current = time;
        const charsToAdd = deltaTime * charsPerSecond;
        displayedLengthRef.current = Math.min(
          text.length,
          displayedLengthRef.current + charsToAdd,
        );
        setDisplayedText(text.slice(0, Math.floor(displayedLengthRef.current)));
      } else {
        lastFrameTimeRef.current = 0;
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [text]);

  return displayedText;
}

// ── Object definition helper ──────────────────────────────────────────────────

/**
 * Get object definition from typename.
 */
export function getObjectDef(typename: string): ObjectType<any> | undefined {
  return Objects[typename as keyof typeof Objects] as
    | ObjectType<any>
    | undefined;
}

export type StreamContext = {
  identifier: string;
  node: {
    id: string;
    identifier?: string;
    __typename?: string;
    [key: string]: unknown;
  };
};

export type StreamingContent = {
  identifier: string;
  order?: number | null;
  type?: string | null;
  status?: string | null;
  streamingAgentText?: string | null;
  toolName?: string | null;
  toolInput?: Record<string, unknown> | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
  context?: StreamContext[] | null;
  parentIdentifier?: string | null;
  executionMode?: string | null;
  iterationIndex?: number | null;
  pendingChange?: { identifier: string; changeType: string } | null;
  pendingSessionId?: string | null;
  children?: StreamingContent[];
};

export type UnifiedContentItem = {
  id?: string;
  identifier?: string;
  /** When the row first showed something. */
  createdAt?: string | null;
  type?: string | null;
  status?: string | null;
  text?: string | null;
  toolName?: string | null;
  toolInput?: Record<string, unknown> | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
  context?: StreamContext[] | MessageContextObject[] | null;
  nodes?: Array<Record<string, unknown>>;
  isStreaming?: boolean;
  isActiveToolCall?: boolean;
  executionMode?: string | null;
  iterationIndex?: number | null;
  parentIdentifier?: string | null;
  children?: UnifiedContentItem[];
  ruleNode?: {
    id?: string;
    name?: string | null;
    __typename?: string;
    summary?: string | null;
    summaryParts?: Array<{
      text: string;
      tone?: string | null;
      breakBefore?: boolean | null;
      indent?: number | null;
      referenceObjectType?: string | null;
      referenceIdentifier?: string | null;
    }> | null;
  } | null;
  execution?: {
    identifier?: string;
    source?: string | null;
    status?: string | null;
  } | null;
  pendingChange?: {
    identifier: string;
    changeType: string;
    modelName?: string;
    status?: string;
  } | null;
  summaryShownExternally?: boolean;
  order?: number | null;
};

export function buildContentTree<
  T extends {
    identifier?: string;
    id?: string;
    order?: number | null;
    parentIdentifier?: string | null;
    children?: T[];
  },
>(flat: T[]): T[] {
  const ordered = [...flat].sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) -
      (b.order ?? Number.MAX_SAFE_INTEGER),
  );
  const byId = new Map<string, T>();
  const roots: T[] = [];
  const cloned = ordered.map((item) => ({ ...item, children: [] as T[] }));
  for (const item of cloned) {
    const key = item.identifier ?? item.id;
    if (key) byId.set(key, item);
  }
  for (const item of cloned) {
    const parentId =
      item.parentIdentifier ??
      (item as { parent_identifier?: string | null }).parent_identifier;
    if (parentId) {
      const parent = byId.get(parentId);
      if (parent) {
        parent.children!.push(item);
        continue;
      }
    }
    roots.push(item);
  }
  return roots;
}

export type ThinkContentItem = {
  id?: string | null;
  identifier?: string | null;
  type?: string | null;
  status?: string | null;
  toolName?: string | null;
  toolInput?: Record<string, unknown> | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
  isStreaming?: boolean;
  isActiveToolCall?: boolean;
  text?: string | null;
};

/**
 * Whether the step this row records is still in progress.
 *
 * The agent writes ``running`` when it opens a row and ``done`` when the step
 * finishes, so liveness is read off the row itself. It used to be inferred by
 * scanning siblings for a later step that had started, which meant every
 * question about one row depended on the shape of all the others.
 */
function isStepRunning(item: ThinkContentItem): boolean {
  return item.status === "running";
}

/**
 * A step the agent takes towards the answer rather than one it hands the user:
 * its own reasoning (``think``), or bringing more services' tools into the
 * turn (``get_service_tools``). Both read as thinking, not as a tool call.
 */
export function isThinkToolCall(item: ThinkContentItem): boolean {
  return (
    item.type === "tool_call" &&
    (item.toolName === "think" || item.toolName === "get_service_tools")
  );
}

export function getThinkTopic(item: ThinkContentItem): string {
  const input = item.toolInput as Record<string, unknown> | null | undefined;
  const output = item.toolOutput as Record<string, unknown> | null | undefined;
  const topic = output?.topic ?? input?.topic;
  return typeof topic === "string" && topic.trim() ? topic.trim() : "Thinking";
}

export function getThinkBody(item: ThinkContentItem): string {
  const output = item.toolOutput as Record<string, unknown> | null | undefined;
  const input = item.toolInput as Record<string, unknown> | null | undefined;

  if (isThinkToolActive(item)) {
    if (typeof input?.reasoning === "string" && input.reasoning.trim()) {
      return input.reasoning.trim();
    }
    if (typeof input?.context === "string" && input.context.trim()) {
      return input.context.trim();
    }
    if (typeof item.text === "string" && item.text.trim()) {
      return item.text.trim();
    }
    return "";
  }

  if (typeof output?.summary === "string" && output.summary.trim()) {
    return output.summary.trim();
  }
  if (typeof input?.reasoning === "string" && input.reasoning.trim()) {
    return input.reasoning.trim();
  }
  if (typeof input?.context === "string" && input.context.trim()) {
    return input.context.trim();
  }
  return "";
}

export function isThinkToolActive(item: ThinkContentItem): boolean {
  return isThinkToolCall(item) && !item.toolError && isStepRunning(item);
}

export function isQueryToolActive(item: ThinkContentItem): boolean {
  return isQueryToolCall(item) && !item.toolError && isStepRunning(item);
}

/** An explore session is open while its query row is still running. */
export function isExploreSessionOpen(item: ThinkContentItem): boolean {
  return isQueryToolActive(item);
}

/** Walk the content tree for an open explore session (suppresses planning rows). */
export function hasActiveExploreInTree(items: UnifiedContentItem[]): boolean {
  return items.some(
    (item) =>
      isExploreSessionOpen(item) ||
      (!!item.children?.length && hasActiveExploreInTree(item.children)),
  );
}

export function resolveActiveToolCall(item: ThinkContentItem): boolean {
  return item.type === "tool_call" && !item.toolError && isStepRunning(item);
}

export function isQueryToolCall(item: ThinkContentItem): boolean {
  return !!item.toolName?.startsWith("query_");
}

function isPresentResultsTool(item: ThinkContentItem): boolean {
  return item.toolName === "present_results";
}

function readPresentToUser(
  input: Record<string, unknown> | null | undefined,
  output: Record<string, unknown> | null | undefined,
): boolean | undefined {
  if (typeof output?.present_to_user === "boolean") {
    return output.present_to_user;
  }
  if (typeof input?.present_to_user === "boolean") {
    return input.present_to_user;
  }
  return undefined;
}

/** Internal query_* rows — shown inline, not as full tool cards. */
export function isGatedInternalQuery(item: ThinkContentItem): boolean {
  return isQueryToolCall(item);
}

function getQueryServiceName(item: ThinkContentItem): string {
  if (isPresentResultsTool(item)) {
    const input = item.toolInput as Record<string, unknown> | null | undefined;
    const serviceName = input?.service_name;
    if (typeof serviceName === "string" && serviceName.trim()) {
      return serviceName.trim().replace(/_/g, " ");
    }
  }
  if (!item.toolName?.startsWith("query_")) return "records";
  return item.toolName.slice("query_".length).replace(/_/g, " ");
}

/** Agent-facing explore label — intent summary, not result counts. */
export function getExploreIntentLabel(item: ThinkContentItem): string {
  const input = item.toolInput as Record<string, unknown> | null | undefined;
  const output = item.toolOutput as Record<string, unknown> | null | undefined;

  const fromInput = input?.exploration_summary;
  if (typeof fromInput === "string" && fromInput.trim()) {
    return fromInput.trim();
  }

  const fromOutput = output?.explore_summary;
  if (typeof fromOutput === "string" && fromOutput.trim()) {
    return fromOutput.trim().replace(/ · \d+ results?$/i, "");
  }

  return getQueryServiceName(item);
}

function getExploreTopic(item: ThinkContentItem): string {
  return getQueryServiceName(item);
}

/**
 * Internal explore diary only — committed attempt log plus the in-flight note
 * while a query is actively running. User-facing narration lives on text rows.
 */
export function getExploreBody(item: ThinkContentItem): string {
  const input = item.toolInput as Record<string, unknown> | null | undefined;
  const output = item.toolOutput as Record<string, unknown> | null | undefined;
  const parts: string[] = [];

  const log = output?.exploration_log;
  if (Array.isArray(log)) {
    for (const line of log) {
      if (typeof line === "string" && line.trim()) {
        parts.push(line.trim());
      }
    }
  }

  if (isQueryToolActive(item)) {
    const streamingNote = input?.exploration_note;
    if (typeof streamingNote === "string" && streamingNote.trim()) {
      const note = streamingNote.trim();
      if (parts.length === 0 || parts[parts.length - 1] !== note) {
        parts.push(note);
      }
    }
  }

  return parts.join("\n\n");
}

/**
 * A finished search says what came back, so "found nothing" cannot read the
 * same as "found five"; one still running claims no count.
 */
export function getExploreHeader(item: ThinkContentItem): string {
  const header = `Explored · ${getExploreIntentLabel(item)}`;
  const count = (item.toolOutput as Record<string, unknown> | null | undefined)
    ?.count;
  if (typeof count !== "number" || isQueryToolActive(item)) return header;
  return `${header} · ${count} ${count === 1 ? "result" : "results"}`;
}

function isWorkflowActionThreadNodeEval(item: UnifiedContentItem): boolean {
  return (
    item.type === "node_eval" &&
    item.ruleNode?.__typename === "RuleNodeActionThreadObject"
  );
}

export function getRuleStepMessageText(item: UnifiedContentItem): string {
  if (isWorkflowActionThreadNodeEval(item)) {
    const output = item.toolOutput as
      | Record<string, unknown>
      | null
      | undefined;
    if (typeof output?.message === "string" && output.message.trim()) {
      return output.message.trim();
    }
    const text = item.text?.trim() ?? "";
    if (!text) return "";
    const sendPrefix = 'Send: "';
    if (text.startsWith(sendPrefix) && text.endsWith('"')) {
      return text.slice(sendPrefix.length, -1);
    }
    return text;
  }
  return item.text?.trim() || item.ruleNode?.summary?.trim() || "";
}

export function shouldRenderWorkflowStepSummaryParts(
  item: UnifiedContentItem,
): boolean {
  if (isWorkflowActionThreadNodeEval(item)) return false;
  return (item.ruleNode?.summaryParts?.length ?? 0) > 0;
}

/** Message-level workflow step (denormalized ``Message.workflow_node``). */
export function messageIsWorkflowStep(message: unknown): boolean {
  return (
    typeof message === "object" &&
    message != null &&
    "ruleNode" in message &&
    (message as { ruleNode?: unknown }).ruleNode != null
  );
}

export function getRuleStepSummaryItem(
  content: UnifiedContentItem[],
): UnifiedContentItem | null {
  if (content.length === 0) return null;
  const first = content[0];
  if (first.type !== "node_eval") {
    const withText = content.find((c) => c.text?.trim());
    if (withText) return withText;
    if (content.length === 1 && (first.context?.length ?? 0) > 0) return first;
    return null;
  }
  if (content.length !== 1) return null;
  const item = first;
  if (isWorkflowActionThreadNodeEval(item)) {
    return getRuleStepMessageText(item) ? item : null;
  }
  const hasSummary =
    !!item.text?.trim() ||
    (item.ruleNode?.summaryParts?.length ?? 0) > 0 ||
    !!item.ruleNode?.summary?.trim();
  return hasSummary ? item : null;
}

export function workflowStepForResults(
  item: UnifiedContentItem,
): UnifiedContentItem {
  return {
    ...item,
    text: undefined,
    summaryShownExternally: true,
    ruleNode: item.ruleNode
      ? {
          ...item.ruleNode,
          summary: undefined,
          summaryParts: undefined,
        }
      : item.ruleNode,
  };
}

/** Matches markdown list item lines (unordered or ordered). */
const LIST_ITEM_LINE_RE = /^\s*([-+*]|\d+\.)\s+/;

export function isListItemLine(line: string): boolean {
  return LIST_ITEM_LINE_RE.test(line.trim());
}

/** True when every non-empty line is a list marker or indented continuation. */
export function isListBlock(content: string): boolean {
  const lines = content.split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return false;
  return lines.every((line) => isListItemLine(line) || /^\s{2,}\S/.test(line));
}

function shouldMergeListParagraph(prev: string, next: string): boolean {
  if (!isListItemLine(next) && !isListBlock(next)) return false;
  return prev.split("\n").some(isListItemLine);
}

/**
 * Split message text into markdown blocks without breaking list structure.
 * Consecutive paragraphs that are single list items (common LLM formatting
 * with blank lines between bullets) are merged into one list block.
 */
export function splitTextBlocks(text: string): string[] {
  const rawParts = text.split(/\n\n+/);
  const blocks: string[] = [];

  for (const part of rawParts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const prev = blocks[blocks.length - 1];
    if (prev && shouldMergeListParagraph(prev, trimmed)) {
      blocks[blocks.length - 1] = `${prev}\n${trimmed}`;
      continue;
    }

    blocks.push(trimmed);
  }

  return blocks;
}

type ReferenceLineGroup =
  | { type: "table"; content: string }
  | { type: "markdown"; content: string }
  | { type: "line"; content: string };

/** Group lines for the reference-aware path (tables and list blocks). */
export function groupLinesWithReferences(
  lines: string[],
): ReferenceLineGroup[] {
  const groups: ReferenceLineGroup[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith("|")) {
      const tableLines = [line];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith("|")) {
        i++;
        tableLines.push(lines[i]);
      }
      groups.push({ type: "table", content: tableLines.join("\n") });
      i++;
      continue;
    }

    if (isListItemLine(line)) {
      const listLines = [line];
      i++;
      while (i < lines.length) {
        const next = lines[i];
        if (next.trim() === "") {
          i++;
          continue;
        }
        if (isListItemLine(next) || /^\s{2,}\S/.test(next)) {
          listLines.push(next);
          i++;
          continue;
        }
        break;
      }
      groups.push({ type: "markdown", content: listLines.join("\n") });
      continue;
    }

    groups.push({ type: "line", content: line });
    i++;
  }

  return groups;
}

function mapChild(item: UnifiedContentItem): ToolCallContent {
  return {
    identifier: item.identifier ?? item.id ?? "",
    type: item.type,
    status: item.status,
    text: item.text,
    streamingAgentText: null,
    toolName: item.toolName,
    toolInput: item.toolInput,
    toolOutput: item.toolOutput,
    toolError: item.toolError,
    executionMode: item.executionMode,
    iterationIndex: item.iterationIndex,
    children: item.children?.map(mapChild),
    isStreaming: item.isStreaming,
    isActiveToolCall: resolveActiveToolCall(item),
    pendingChangeIdentifier: item.pendingChange?.identifier ?? null,
    pendingChangeType: item.pendingChange?.changeType ?? null,
    summaryShownExternally: item.summaryShownExternally,
    ruleNode: item.ruleNode ?? null,
  };
}

/** Map unified thread content into the shape expected by ToolCallCard. */
export function toToolCallContent(
  item: UnifiedContentItem,
  index: number,
): ToolCallContent {
  return {
    identifier: item.identifier ?? item.id ?? String(index),
    type: item.type,
    status: item.status,
    text: item.text,
    streamingAgentText: null,
    toolName: item.toolName,
    toolInput: item.toolInput,
    toolOutput: item.toolOutput,
    toolError: item.toolError,
    executionMode: item.executionMode,
    iterationIndex: item.iterationIndex,
    resolvedNodes: (item.nodes ?? null) as ToolCallContent["resolvedNodes"],
    children: item.children?.map(mapChild),
    isStreaming: item.isStreaming,
    isActiveToolCall:
      item.type === "node_eval" ? false : resolveActiveToolCall(item),
    pendingChangeIdentifier: item.pendingChange?.identifier ?? null,
    pendingChangeType: item.pendingChange?.changeType ?? null,
    summaryShownExternally: item.summaryShownExternally,
    ruleNode: item.ruleNode ?? null,
  };
}

type InlineToolRowOptions = {
  item: ThinkContentItem;
  /** How long the turn thought before this row, when the row is the first one. */
  thoughtSeconds?: number | null;
};

/** Think / gated-query rows rendered inline (not as accordion cards). */
export function renderInlineToolRow({
  item,
  thoughtSeconds,
}: InlineToolRowOptions): ReactNode | null {
  if (isThinkToolCall(item))
    return <ThoughtActionRow item={item} thoughtSeconds={thoughtSeconds} />;
  if (isGatedInternalQuery(item)) return <ExploreActionRow item={item} />;
  return null;
}

/** What a ``chart`` call is asked for, as the agent wrote it. */
export type ToolCallChartSpec = {
  title?: string;
  source?: string;
  group_by?: Array<{ field?: string; interval?: string | null }>;
  aggregate?: Array<{ op?: string; field?: string | null }>;
  filters?: Array<Record<string, unknown>> | Record<string, unknown> | null;
  search?: string | null;
  order_by?: string[] | null;
};

/**
 * The spec a card should draw a chart from, or ``null`` if it should not.
 *
 * A call still running has nothing to plot from yet — an empty chart reads as
 * a broken one — and a call that failed says so instead.
 */
export function toChartSpec(
  toolName: string | null | undefined,
  toolInput: Record<string, unknown> | null | undefined,
): ToolCallChartSpec | null {
  if (toolName !== "chart" || !toolInput) return null;
  const spec = toolInput as ToolCallChartSpec;
  if (!spec.source || !spec.group_by?.length) return null;
  return spec;
}
